import React from "react";
import { EyeIcon, FolderIcon } from "lucide-react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { toDetailedDateTimeFormat } from "@/helpers/dates";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { Link } from "react-router";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useGetUserQuery } from "@/api/users-admin";
import { BusinessReportsOutcomeListRowStatus } from "@/modules/snapshots/outcomeListRowStatus";
import { useGetBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";

interface ItemRowProps {
  item: BusinessReportOutcome;
}

const ItemRow: React.FC<ItemRowProps> = ({ item }) => {
  const { data: owner } = useGetUserQuery(item.owner_id);
  const formattedDate = toDetailedDateTimeFormat(item.created_at);
  if (!item.fs_entry) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-3 p-4 hover:bg-gray-50 sm:p-6 2xl:flex-row 2xl:items-center 2xl:justify-between 2xl:space-x-4 2xl:space-y-0">
      <div className="flex min-w-0 items-center space-x-3">
        <div className="flex-shrink-0">
          {item.fs_entry.type === "folder" ? (
            <FolderIcon className="h-6 w-6 text-yellow-500" />
          ) : (
            <DocumentTextIcon className="h-6 w-6 text-blue-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-gray-900">{item.business_report.name}</p>
              <BusinessReportsOutcomeListRowStatus status={item.status} />
            </div>
            <p className="mt-1 truncate text-xs text-gray-600" title={item.fs_entry.document_name}>
              {item.fs_entry.document_name}
            </p>
            <div className="mt-1 flex items-center space-x-2">
              <Avatar className="h-5 w-5">
                <AvatarImage
                  src={owner?.avatar ? createBase64Image(owner.avatar) : defaultUserAvatar}
                  alt={owner?.name || "User"}
                />
                <AvatarFallback className="text-[10px]">{owner ? owner.name[0] : "U"}</AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium text-gray-700">{owner?.name || owner?.username}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500">Created: {formattedDate}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
        <Button variant="secondary" size="sm" className="justify-center" asChild>
          <Link to={`/drive/${item.fs_entry.parent_id}`}>
            <FolderIcon className="mr-2 h-4 w-4" />
            Show in Folder
          </Link>
        </Button>
        <Button variant="secondary" size="sm" className="justify-center" asChild>
          <Link to={`/reports/outcome/${item.id}`}>
            <EyeIcon className="mr-2 h-4 w-4" />
            View Snapshot
          </Link>
        </Button>
      </div>
    </div>
  );
};

export const SnapshotsTableRecentItems: React.FC = () => {
  const { data } = useGetBusinessReportsOutcomeQuery();
  if (!data) {
    return null;
  }

  const { results: snapshots = [] } = data;

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <ul className="divide-y divide-gray-200">
        {snapshots.slice(0, 5).map(snapshot => (
          <li key={snapshot.id}>
            <ItemRow item={snapshot} />
          </li>
        ))}
        {snapshots.length === 0 && (
          <li className="px-4 py-5 text-center text-sm text-gray-500 sm:px-6">No recent snapshots found.</li>
        )}
      </ul>
    </div>
  );
};
